/**
 * ArrowStepperWorkflow - Stepper com formas de seta para progresso de fluxo de trabalho.
 *
 * Exibe etapas em formato de setas/chevrons apontando para direita,
 * indicando o progresso visual do processo.
 *
 * @example
 * <ArrowStepperWorkflow
 *   etapas={[
 *     { key: 'analise', label: 'Análise' },
 *     { key: 'diligencia', label: 'Diligência' },
 *     { key: 'aprovacao', label: 'Aprovação' },
 *   ]}
 *   etapaAtual="diligencia"
 * />
 */

import React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// TIPOS
// =============================================================================

interface Etapa {
  key: string;
  label: string;
}

export interface ArrowStepperWorkflowProps {
  /** Lista de etapas do fluxo */
  etapas: Etapa[];
  /** Chave da etapa atualmente ativa */
  etapaAtual: string;
  /** Status adicional (não utilizado visualmente por padrão, disponível para extensão) */
  status?: string;
  /** Classes CSS adicionais para o container */
  className?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

type EtapaEstado = 'concluida' | 'atual' | 'futura';

function getEtapaEstado(
  etapaKey: string,
  etapaAtual: string,
  etapas: Etapa[]
): EtapaEstado {
  const indexAtual = etapas.findIndex((e) => e.key === etapaAtual);
  const indexEtapa = etapas.findIndex((e) => e.key === etapaKey);

  if (indexEtapa < indexAtual) return 'concluida';
  if (indexEtapa === indexAtual) return 'atual';
  return 'futura';
}

// Clip-path para a primeira seta (sem recuo no lado esquerdo)
const CLIP_FIRST =
  'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%)';

// Clip-path para etapas intermediárias e última (com recuo no lado esquerdo)
const CLIP_MIDDLE =
  'polygon(0% 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 0% 100%, 12px 50%)';

// =============================================================================
// SUBCOMPONENTE: ArrowStep
// =============================================================================

interface ArrowStepProps {
  etapa: Etapa;
  estado: EtapaEstado;
  isFirst: boolean;
  isLast: boolean;
  /** Etapa deve ser visível em mobile */
  visibleOnMobile: boolean;
}

function ArrowStep({ etapa, estado, isFirst, visibleOnMobile }: ArrowStepProps) {
  const clipPath = isFirst ? CLIP_FIRST : CLIP_MIDDLE;

  return (
    <div
      className={cn(
        'relative flex-1 min-w-0 flex items-center justify-center h-10',
        'transition-all duration-200',
        // Em mobile, mostrar apenas as etapas relevantes
        !visibleOnMobile && 'hidden sm:flex'
      )}
      style={{ clipPath }}
      title={etapa.label}
      aria-current={estado === 'atual' ? 'step' : undefined}
    >
      {/* Fundo colorido por estado */}
      <div
        className={cn(
          'absolute inset-0 transition-colors duration-200',
          estado === 'concluida' && 'bg-primary/80',
          estado === 'atual' && 'bg-primary',
          estado === 'futura' && 'bg-muted'
        )}
      />

      {/* Texto da etapa */}
      <span
        className={cn(
          'relative z-10 px-4 text-xs font-medium leading-tight text-center truncate max-w-full select-none',
          estado === 'concluida' && 'text-white',
          estado === 'atual' && 'text-primary-foreground',
          estado === 'futura' && 'text-muted-foreground'
        )}
      >
        {etapa.label}
      </span>
    </div>
  );
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

export function ArrowStepperWorkflow({
  etapas,
  etapaAtual,
  className,
}: ArrowStepperWorkflowProps) {
  if (!etapas || etapas.length === 0) return null;

  const indexAtual = etapas.findIndex((e) => e.key === etapaAtual);

  return (
    <nav
      aria-label="Progresso do fluxo"
      className={cn('w-full', className)}
    >
      {/* Container desktop: todas as etapas lado a lado */}
      <div className="flex gap-0.5 w-full overflow-hidden">
        {etapas.map((etapa, index) => {
          const estado = getEtapaEstado(etapa.key, etapaAtual, etapas);
          const isFirst = index === 0;
          const isLast = index === etapas.length - 1;

          // Em mobile, mostrar etapa anterior (se existir), atual e próxima (se existir)
          const visibleOnMobile =
            index === indexAtual ||
            index === indexAtual - 1 ||
            index === indexAtual + 1;

          return (
            <ArrowStep
              key={etapa.key}
              etapa={etapa}
              estado={estado}
              isFirst={isFirst}
              isLast={isLast}
              visibleOnMobile={visibleOnMobile}
            />
          );
        })}
      </div>

      {/* Indicador de progresso em texto para mobile */}
      <div className="sm:hidden mt-2 text-center">
        <span className="text-xs text-muted-foreground">
          Etapa{' '}
          <span className="font-semibold text-foreground">
            {indexAtual + 1}
          </span>{' '}
          de{' '}
          <span className="font-semibold text-foreground">{etapas.length}</span>
          {' — '}
          <span className="font-medium text-primary">
            {etapas[indexAtual]?.label ?? ''}
          </span>
        </span>
      </div>
    </nav>
  );
}

export default ArrowStepperWorkflow;
